import React, { useEffect, useState, useRef } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaFilter, FaEye } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { hasPermission } from '../../utils/permissionHelper';
import UserFilters from '../../components/commonComponent/UserFilters';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const FamilyIndex = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(() => {
    const stored = localStorage.getItem('family_page_limit');
    return stored ? parseInt(stored) : 10;
  });

  // Filter State
  const [filters, setFilters] = useState({
    clusterId: '',
    chapterId: '',
    cityId: '',
    stateId: '',
    businessCategoryId: '',
    businessSubCategoryId: '',
    startDate: '',
    endDate: '',
    dateField: '',
  });

  const [dropdowns, setDropdowns] = useState({
    clusters: [],
    chapters: [],
    cities: [],
    states: [],
    businessCategories: [],
    businessSubCategories: [],
  });

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormValues, setCreateFormValues] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    mobileNumber: '',
    phone_code: '91',
    iso_code: 'IN',
    email: '',
    business_name: '',
    business_category: '',
    business_sub_category: [],
    joiningDate: new Date().toISOString().split('T')[0],
    userType: 'Family',
    fees: '',
    plan: '',
    planName: '',
    source: '',
    invitedByUser: '',
    dob: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [businessDropdowns, setBusinessDropdowns] = useState({
    businessCategories: [],
    businessSubCategories: [],
  });
  const [allUsers, setAllUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  // Filter visibility (hidden by default)
  const [showFilters, setShowFilters] = useState(false);

  // Import modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const importInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);

  const tableContainerRef = useRef(null);
  const columnDropdownRef = useRef(null);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  // Track click outside dropdown & Escape key
  useEffect(() => {
    if (!showColumnFilters) return;

    const handleClickOutside = e => {
      // Close if clicking anywhere on the card
      if (tableContainerRef.current && tableContainerRef.current.contains(e.target)) {
        if (!columnDropdownRef.current || !columnDropdownRef.current.contains(e.target)) {
          setShowColumnFilters(false);
        }
      } else if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(e.target)
      ) {
        setShowColumnFilters(false);
      }
    };
    const handleEsc = e => {
      if (e.key === 'Escape') setShowColumnFilters(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showColumnFilters]);

  const allColumns = [
    { key: 'memberId', label: 'Member ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'mobileNumber', label: 'Mobile Number' },
    { key: 'chapter', label: 'Chapter' },
    { key: 'city', label: 'City' },
    { key: 'joiningDate', label: 'Joining Date' },
    { key: 'roles', label: 'Role' },
    { key: 'businessName', label: 'Business Name' },
    { key: 'businessCategory', label: 'Business Category' },
    { key: 'businessSubCategory', label: 'Business SubCategory' },
  ];

  const getInitialVisibleColumns = () => {
    const stored = localStorage.getItem('family_visible_columns');
    if (stored) return JSON.parse(stored);
    // Hide memberId by default
    return allColumns.filter(c => c.key !== 'memberId').map(c => c.key);
  };
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);

  useEffect(() => {
    localStorage.setItem('family_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const [
        clustersRes,
        chaptersRes,
        citiesRes,
        statesRes,
        catRes,
        subCatRes,
      ] = await Promise.all([
        axios.get('/admin/api/clusters/all'),
        axios.get('/admin/api/chapters/all'),
        axios.get('/admin/api/city'),
        axios.get('/admin/api/state'),
        axios.get('/admin/api/businessCategory'),
        axios.get('/admin/api/businessSubCategory'),
      ]);
      setDropdowns({
        clusters: clustersRes.data?.cluster?.map(c => ({ value: c._id, label: c.name })) || [],
        chapters: chaptersRes.data?.chapter?.map(ch => ({ value: ch._id, label: ch.name })) || [],
        cities: citiesRes.data?.cities?.map(ci => ({ value: ci._id, label: ci.name })) || [],
        states: statesRes.data?.states?.map(st => ({ value: st._id, label: st.name })) || [],
        businessCategories: catRes.data?.businessCategories?.map(c => ({ value: c._id, label: c.name })) || [],
        businessSubCategories: subCatRes.data?.businessSubCategories?.map(sc => ({ value: sc._id, label: sc.name })) || [],
      });
    } catch (err) {
      toastError('Failed to load filter dropdowns');
    }
  };

  useEffect(() => {
    localStorage.setItem('family_page_limit', limit);
  }, [limit]);

  useEffect(() => {
    if (hasPermission(user, 'Family', 'Read')) {
      fetchUsers(currentPage, searchQuery, filters);
    }
    // eslint-disable-next-line
  }, [currentPage, limit]);

  useEffect(() => {
    if (!hasPermission(user, 'Family', 'Read')) return;
    const handler = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchUsers(1, searchQuery, filters);
      }
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line
  }, [searchQuery, filters]);

  const fetchUsers = async (page = 1, search = '', filtersData = filters) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
      });
      if (search && search.trim()) params.append('search', search.trim());
      if (filtersData.dateField) params.append('dateField', filtersData.dateField);
      if (filtersData.clusterId) params.append('clusterId', filtersData.clusterId);
      if (filtersData.chapterId) params.append('chapterId', filtersData.chapterId);
      if (filtersData.cityId) params.append('cityId', filtersData.cityId);
      if (filtersData.stateId) params.append('stateId', filtersData.stateId);
      if (filtersData.businessCategoryId) params.append('businessCategoryId', filtersData.businessCategoryId);
      if (filtersData.businessSubCategoryId) params.append('businessSubCategoryId', filtersData.businessSubCategoryId);
      if (filtersData.startDate) params.append('startDate', filtersData.startDate);
      if (filtersData.endDate) params.append('endDate', filtersData.endDate);
      const res = await axios.get(`/api/user?${params.toString()}`);
      const { users = [], totalCount = 0 } = res.data;
      setUsers(users);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch families');
    }
  };

  const formatCell = val => {
    if (React.isValidElement(val)) return val;
    if (val === null || val === undefined) return '-';
    if (typeof val === 'object') {
      if (Array.isArray(val))
        return val.map(item =>
          typeof item === 'object' ? item.name || item._id || JSON.stringify(item) : item
        ).join(', ');
      if (val.name) return val.name;
      if (val._id) return val._id;
      try {
        return JSON.stringify(val);
      } catch {
        return '-';
      }
    }
    return val;
  };

  const handleCreateFamily = async () => {
    const mobile = createFormValues.mobileNumber;
    if (!mobile || !/^\d{10}$/.test(mobile)) {
      toastError('Mobile number must be exactly 10 digits and numeric only.');
      setCreateLoading(false);
      return;
    }
    setCreateLoading(true);
    try {
      const payload = {
        firstName: createFormValues.firstName,
        lastName: createFormValues.lastName,
        middleName: createFormValues.middleName,
        iso_code: createFormValues.iso_code,
        phone_code: createFormValues.phone_code,
        mobileNumber: createFormValues.mobileNumber,
        email: (createFormValues.email || '').toLowerCase(),
        joiningDate: createFormValues.joiningDate,
        userType: 'Family',
        business: [
          {
            name: createFormValues.business_name,
            category: createFormValues.business_category,
            subCategory: Array.isArray(createFormValues.business_sub_category)
              ? createFormValues.business_sub_category.join(',')
              : createFormValues.business_sub_category || '',
          },
        ],
        plan: createFormValues.plan,
        fees: createFormValues.fees,
        source: createFormValues.source,
        invitedByUser: createFormValues.invitedByUser,
        dob: createFormValues.dob,
      };

      const res = await axios.post('/api/user/create', payload);

      if (res.data.Status === 1) {
        toastSuccess(res.data.Message || 'User created successfully!');
        setShowCreateModal(false);
        setCreateFormValues({
          firstName: '',
          lastName: '',
          middleName: '',
          mobileNumber: '',
          phone_code: '91',
          iso_code: 'IN',
          email: '',
          business_name: '',
          business_category: '',
          business_sub_category: [],
          joiningDate: new Date().toISOString().split('T')[0],
          plan: '',
          planName: '',
          fees: '',
          source: '',
          invitedByUser: '',
          dob:'',
        });
        setCurrentPage(1);
        fetchUsers(1, searchQuery, filters);
      } else {
        if (res.data.Message === 'User already exists') {
          toastError('A user with this email or mobile number already exists.');
        } else if (Array.isArray(res.data.errors) && res.data.errors.length > 0) {
          toastError(res.data.errors[0]);
        } else {
          toastError(res.data.Message || 'Failed to create user');
        }
      }
    } catch (err) {
      const Message =
        err.response?.data?.Message ||
        err.response?.data?.error ||
        'Failed to create user';
      toastError(Message);
    }
    setCreateLoading(false);
  };

  // Delete Family
  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this Family?')) return;
    try {
      await axios.delete(`/api/user/${id}?userType=Family`);
      toastSuccess('Family deleted successfully!');
      setCurrentPage(1);
      fetchUsers(1, searchQuery, filters);
    } catch {
      toastError('Failed to delete family');
    }
  };

  // Reset/clear filter handler
  const clearFilters = () => {
    setFilters({
      clusterId: '',
      chapterId: '',
      cityId: '',
      stateId: '',
      businessCategoryId: '',
      businessSubCategoryId: '',
      startDate: '',
      endDate: '',
      dateField: '',
    });
    setSearchQuery('');
    setCurrentPage(1);
    fetchUsers(1, '', {});
  };

  // Modal dropdown fetching/other
  useEffect(() => {
    if (!showCreateModal) return;
    const fetchBusinessDropdowns = async () => {
      try {
        const [catRes, subCatRes] = await Promise.all([
          axios.get('/admin/api/businessCategory'),
          axios.get('/admin/api/businessSubCategory'),
        ]);
        setBusinessDropdowns({
          businessCategories: catRes.data?.businessCategories?.map(c => ({
            value: c._id, label: c.name,
          })) || [],
          businessSubCategories: subCatRes.data?.businessSubCategories?.map(sc => ({
            value: sc._id, label: sc.name, category: sc.category,
          })) || [],
        });
      } catch {
        setBusinessDropdowns({ businessCategories: [], businessSubCategories: [] });
      }
    };

    const fetchPlans = async () => {
      try {
        const res = await axios.get('/api/plan?userType=Family');
        const planOptions = res.data.plans?.map(p => ({
          value: p._id, label: p.name,
        })) || [];
        setPlans(planOptions);
        if (planOptions.length > 0)
          setCreateFormValues(v => ({
            ...v,
            plan: planOptions[0].value,
            planName: planOptions[0].label,
          }));
        else
          setCreateFormValues(v => ({
            ...v,
            plan: '',
            planName: '',
          }));
      } catch {
        setPlans([]);
        setCreateFormValues(v => ({
          ...v,
          plan: '',
          planName: '',
        }));
      }
    };

    const fetchAllUsers = async () => {
      try {
        const params = new URLSearchParams({ page: 1, limit: 1000 });
        const res = await axios.get(`/api/user?${params.toString()}`);
        setAllUsers(res.data.users || []);
      } catch {
        setAllUsers([]);
      }
    };

    fetchBusinessDropdowns();
    fetchPlans();
    fetchAllUsers();
  }, [showCreateModal]);

  // Modal change handler for business category
  const handleBusinessCategoryChange = async selectedOption => {
    const categoryId = selectedOption ? selectedOption.value : '';
    setCreateFormValues(v => ({
      ...v,
      business_category: categoryId,
      business_sub_category: [],
    }));
    if (categoryId) {
      try {
        const response = await axios.get(`/admin/api/businessSubCategory?categoryId=${categoryId}`);
        setBusinessDropdowns(d => ({
          ...d,
          businessSubCategories: response.data?.businessSubCategories?.map(sc => ({
            value: sc._id, label: sc.name, category: sc.category,
          })) || [],
        }));
      } catch {
        setBusinessDropdowns(d => ({ ...d, businessSubCategories: [] }));
      }
    } else {
      setBusinessDropdowns(d => ({ ...d, businessSubCategories: [] }));
    }
  };

  // Import modal handlers
  const handleImportFileChange = e => {
    const file = e?.target?.files?.[0] || null;
    setImportFile(file);
  };

  const handleCloseImportModal = () => {
    setImportModalVisible(false);
    setImportFile(null);
    if (importInputRef.current) importInputRef.current.value = null;
  };

  const handleDownloadSample = () => {
    const csv =
      'firstName,middleName,lastName,mobileNumber,email,business_name,business_category,business_sub_category,joiningDate,plan,fees,source,invitedByUser\n' +
      'John,D,Smith,9999999999,john@example.com,Sample Business,Category1,Sub1|Sub2,2025-01-01,DefaultPlan,0,socialMedia,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'family_sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportSubmit = async e => {
    e.preventDefault();
    if (!importFile) {
      toastError('Please select a file to import');
      return;
    }
    const formData = new FormData();
    formData.append('file', importFile);
    try {
      const res = await axios.post('/api/user/import?userType=Family', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.Status === 1) {
        toastSuccess(res.data.Message || 'Import successful');
        handleCloseImportModal();
        setCurrentPage(1);
        fetchUsers(1, searchQuery, filters);
      } else {
        toastError(res.data?.Message || 'Import failed');
      }
    } catch (err) {
      const Message = err.response?.data?.Message || 'Import failed';
      toastError(Message);
    }
  };

  if (!hasPermission(user, 'Family', 'Read')) {
    return (
      <div className="container py-4 text-center">
        <p className="text-danger fw-bold">
          🚫 You don’t have permission to view Families.
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark">Member</h3>
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        {/* Filter toggle */}
        <div>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={() => setShowFilters(prev => !prev)}
          >
            <FaFilter />
          </button>
        </div>
        {/* Search box */}
        <div>
          <input
            type="search"
            className="form-control"
            placeholder="Search by name, mobile, email..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        {/* Column visibility dropdown anchored below icon, closes on card click */}
        <div className="text-end">
          <div ref={columnDropdownRef} style={{ display: 'inline-block', position: 'relative' }}>
            <button
              className="btn btn-sm btn-success d-flex align-items-center gap-1"
              style={{ marginBottom: 8 }}
              onClick={e => {
                setShowColumnFilters(!showColumnFilters);
              }}
              type="button"
            >
              <FaEye />
            </button>
            {/* Dropdown menu always below icon */}
            {showColumnFilters && (
              <div
                className="dropdown-menu show p-3"
                style={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  top: '100%',
                  left: 0,
                  position: 'absolute',
                  zIndex: 2001,
                  width: 'max-content',
                  minWidth: 220,
                  marginTop: 3,
                }}
              >
                {allColumns.map(col => (
                  <div key={col.key} className="form-check mb-1">
                    <input
                      type="checkbox"
                      id={`col-filter-${col.key}`}
                      className="form-check-input"
                      checked={visibleColumns.includes(col.key)}
                      onChange={e => {
                        if (e.target.checked) {
                          setVisibleColumns(prev => {
                            if (prev.includes(col.key)) return prev;
                            // maintain order
                            const newOrder = [];
                            for (const c of allColumns) {
                              if (prev.includes(c.key) || c.key === col.key) newOrder.push(c.key);
                            }
                            return newOrder;
                          });
                        } else {
                          setVisibleColumns(prev => prev.filter(k => k !== col.key));
                        }
                      }}
                    />
                    <label htmlFor={`col-filter-${col.key}`} className="form-check-label">
                      {col.label}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <UserFilters
          filters={filters}
          dropdowns={dropdowns}
          onFilterChange={(field, value) => {
            // Update filters only. Debounced effect will reset page and trigger fetch.
            setFilters(prev => ({ ...prev, [field]: value }));
          }}
          onClearFilters={clearFilters}
          searchQuery={searchQuery}
          setSearchQuery={q => {
            setSearchQuery(q);
            setCurrentPage(1);
          }}
          showDateFilter={true}
        />
      )}

      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <form
              className="modal-content"
              onSubmit={e => {
                e.preventDefault();
                handleCreateFamily();
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title">Family Registration</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">First Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.firstName}
                      onChange={e => setCreateFormValues(v => ({ ...v, firstName: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Middle Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.middleName}
                      onChange={e => setCreateFormValues(v => ({ ...v, middleName: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Last Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.lastName}
                      onChange={e => setCreateFormValues(v => ({ ...v, lastName: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <hr className="my-2" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">
                      Mobile Number<span className="text-danger">*</span>
                    </label>

                    <PhoneInput
                      country={'in'}
                      enableSearch
                      inputClass="form-control"
                      containerClass="w-70"
                      value={`${createFormValues.phoneCode}${createFormValues.mobileNumber}`}
                      onChange={(value, country) => {
                        const dialCode = country.dialCode;
                        const mobile = value.startsWith(dialCode)
                          ? value.slice(dialCode.length)
                          : value;

                        setCreateFormValues(v => ({
                          ...v,
                          iso_code: country.countryCode.toUpperCase(),
                          phoneCode: dialCode,
                          mobileNumber: mobile,
                        }));
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Email ID<span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      value={createFormValues.email}
                      onChange={e => setCreateFormValues(v => ({ ...v, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Joining Date<span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={createFormValues.joiningDate}
                      onChange={e => setCreateFormValues(v => ({ ...v, joiningDate: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <hr className="my-2" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Business Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.business_name}
                      onChange={e => setCreateFormValues(v => ({ ...v, business_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Business Category<span className="text-danger">*</span></label>
                    <Select
                      options={businessDropdowns.businessCategories}
                      placeholder="Select Category"
                      value={
                        businessDropdowns.businessCategories.find(
                          opt => opt.value === createFormValues.business_category
                        ) || null
                      }
                      onChange={handleBusinessCategoryChange}
                      classNamePrefix="select"
                      required={true}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Business Sub Category<span className="text-danger">*</span></label>
                    <Select
                      options={businessDropdowns.businessSubCategories}
                      isMulti
                      placeholder="Select Sub Category"
                      value={businessDropdowns.businessSubCategories.filter(opt =>
                        (createFormValues.business_sub_category || []).includes(opt.value)
                      )}
                      onChange={selectedOptions => {
                        setCreateFormValues(v => ({
                          ...v,
                          business_sub_category: selectedOptions ? selectedOptions.map(opt => opt.value) : [],
                        }));
                      }}
                      classNamePrefix="select"
                      required={true}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Invite By <span className="text-danger">*</span></label>
                    <Select
                      options={[
                        { value: 'socialMedia', label: 'Social Media' },
                        { value: 'user', label: 'User' },
                      ]}
                      placeholder="Select Invite By"
                      value={[
                        { value: 'socialMedia', label: 'Social Media' },
                        { value: 'user', label: 'User' },
                      ].find(opt => opt.value === createFormValues.source) || null}
                      onChange={selectedOption => {
                        setCreateFormValues(v => ({
                          ...v,
                          source: selectedOption ? selectedOption.value : '',
                          invitedByUser: '',
                        }));
                      }}
                      classNamePrefix="select"
                      required={true}
                    />
                  </div>
                  {createFormValues.source === 'user' && (
                    <div className="col-md-4">
                      <label className="form-label">Invited User <span className="text-danger">*</span></label>
                      <Select
                        options={allUsers.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))}
                        placeholder="Select User"
                        value={
                          allUsers.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))
                            .find(opt => opt.value === createFormValues.invitedByUser) || null
                        }
                        onChange={selectedOption => {
                          setCreateFormValues(v => ({
                            ...v,
                            invitedByUser: selectedOption ? selectedOption.value : '',
                          }));
                        }}
                        classNamePrefix="select"
                        required={true}
                      />
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label">Plan <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.planName || ''}
                      readOnly
                      placeholder="No plan available"
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Birth Date<span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={createFormValues.dob}
                      onChange={e => setCreateFormValues(v => ({ ...v, dob: e.target.value }))}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={createLoading}>
                  {createLoading ? 'Save...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importModalVisible && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleImportSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Import Familys</h5>
                <button type="button" className="btn-close" onClick={handleCloseImportModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select File</label>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    className="form-control"
                    ref={importInputRef}
                    onChange={handleImportFileChange}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-info" onClick={handleDownloadSample}>
                  Download Sample File
                </button>
                <button type="submit" className="btn btn-success">Import</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm" ref={tableContainerRef}>
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Member</h5>
          <div>
            {hasPermission(user, 'Family', 'Create') && (
              <button
                className="btn btn-sm btn-success text-white"
                onClick={() => setShowCreateModal(true)}
                type="button"
              >
                + Add Member
              </button>
            )}
          </div>
        </div>
        <div className="table-responsive" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <div className="card-body p-0">
            <table className="table table-bordered table-sm mb-0" style={{ minWidth: 900 }}>
              <thead className="table-light">
                <tr>
                  <th className="py-1">Sr no.</th>
                  {visibleColumns.map(key => {
                    const col = allColumns.find(c => c.key === key);
                    return <th key={key} className="py-1">{col ? col.label : key}</th>;
                  })}
                  <th className="py-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((u, idx) => (
                    <tr key={u._id}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      {visibleColumns.map(key => {
                        let value = '-';
                        if (key === 'memberId') value = u.memberId ||'-';
                        else if (key === 'firstName') value = (
                          <Link to={`/family/view/${u._id}`} style={{ color: '#C44C23', textDecoration: 'underline', cursor: 'pointer' }}>{u.firstName || '-'}</Link>
                        );
                        else if (key === 'middleName') value = u.middleName || '-';
                        else if (key === 'lastName') value = u.lastName || '-';
                        else if (key === 'mobileNumber') value = `+${u.phoneCode || '91'} ${u.mobileNumber || '-'}`;
                        else if (key === 'email') value = u.email || '-';
                        else if (key === 'chapter') value = u.chapter?.name || u.chapter || '-';
                        else if (key === 'city') value = u.city?.name || u.city || '-';
                        else if (key === 'roles') value = u.roles?.name || u.roles || '-';
                        else if (key === 'businessName') value = u.business && u.business.length > 0 ? u.business[0].name || '-' : '-';
                        else if (key === 'businessCategory') value =
                          u.business && u.business.length > 0
                            ? Array.isArray(u.business[0].category)
                              ? u.business[0].category.map(cat => cat.name || cat).join(', ')
                              : (u.business[0].category?.name || u.business[0].category || '-')
                            : '-';
                        else if (key === 'businessSubCategory') value =
                          u.business && u.business.length > 0
                            ? Array.isArray(u.business[0].subCategory)
                              ? u.business[0].subCategory.map(sc => sc.name || sc).join(', ')
                              : (u.business[0].subCategory?.name || u.business[0].subCategory || '-')
                            : '-';
                        else if (key === 'joiningDate') value = u.joiningDate ? new Date(u.joiningDate).toLocaleDateString() : '-';
                        return <td key={key}>{formatCell(value)}</td>;
                      })}
                      <td className="text-center">
                        {hasPermission(user, 'Family', 'Edit') && (
                          <Link
                            to={`/family/edit/${u._id}`}
                            className="btn btn-sm py-0 px-2 fs-9 lh-sm shadow-none border-0 text-primary-custom me-1"
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length + 2} className="text-center">No Familys found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 pt-2">
              <div>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={limit}
                  onChange={e => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
              </div>
              <div className="flex-grow-1"></div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-end align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    &laquo;
                  </button>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 3;
                    pages.push(
                      <button
                        key={1}
                        className={`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}
                        onClick={() => setCurrentPage(1)}
                      >1</button>
                    );
                    if (currentPage > maxVisiblePages + 1)
                      pages.push(<span key="ellipsis1" className="px-2">...</span>);
                    const startPage = Math.max(2, currentPage - 1);
                    const endPage = Math.min(totalPages - 1, currentPage + 1);
                    for (let page = startPage; page <= endPage; page++) {
                      if (page !== 1 && page !== totalPages) {
                        pages.push(
                          <button
                            key={page}
                            className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >{page}</button>
                        );
                      }
                    }
                    if (currentPage < totalPages - maxVisiblePages)
                      pages.push(<span key="ellipsis2" className="px-2">...</span>);
                    if (totalPages > 1) {
                      pages.push(
                        <button
                          key={totalPages}
                          className={`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}
                          onClick={() => setCurrentPage(totalPages)}
                        >{totalPages}</button>
                      );
                    }
                    return pages;
                  })()}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyIndex;